import React from "react";

export const RETRYABLE_INTERVIEW_STATUSES = [
  "COMPLETED",
  "FEEDBACK",
  "STOPPED_BY_APPLICANT",
];

export const RETRY_ANALYSIS_STALE_MS = 5 * 60 * 1000;
export const RETRY_ANALYSIS_BULK_LIMIT = 10;

export function getRetryAnalysisFields(applicant) {
  const session = applicant?.interview_session;
  if (session && typeof session === "object") {
    return {
      sessionId: session.id,
      interviewStatus: session.status,
      analysisStatus: session.analysis_status,
      updatedAt: session.updated_at,
    };
  }
  return {
    sessionId: applicant?.interview_session_id,
    interviewStatus: applicant?.status,
    analysisStatus: applicant?.analysis_status,
    updatedAt: applicant?.updated_at,
  };
}

export function isAnalysisRetryLocked(applicant, now = Date.now()) {
  const { analysisStatus, updatedAt } = getRetryAnalysisFields(applicant);
  if (analysisStatus !== "IN_PROGRESS") {
    return false;
  }
  if (!updatedAt) {
    return true;
  }
  const started = new Date(updatedAt).getTime();
  if (Number.isNaN(started)) {
    return true;
  }
  return now - started < RETRY_ANALYSIS_STALE_MS;
}

export function isAnalysisRetryEligible(applicant, { isJobClosed = false } = {}) {
  const { sessionId, interviewStatus } = getRetryAnalysisFields(applicant);
  if (isJobClosed || !sessionId) {
    return false;
  }
  return RETRYABLE_INTERVIEW_STATUSES.includes(String(interviewStatus || "").toUpperCase());
}

export function getApplicantAtsStage(applicant) {
  return (
    applicant?.ats_current_stage_name ||
    applicant?.interview_session?.candidate?.ats_current_stage_name ||
    ""
  ).trim();
}

/**
 * A retry can move ATS stages only when auto-advance is on (a completion stage is
 * set — that is how the settings toggle derives its state) and a high-match stage
 * is configured. A high-match stage on its own is dead config, and without one a
 * retry has nowhere new to send anyone: the completion stage push already happened
 * during the first analysis.
 */
export function canRetryMoveAtsStages(job) {
  const completionStage = (job?.auto_advance_target_stage || "").trim();
  const highMatchStage = (job?.auto_advance_high_match_stage || "").trim();
  return Boolean(completionStage && highMatchStage);
}

export function getAtsStageUpdateDisabledReason(job) {
  if (canRetryMoveAtsStages(job)) {
    return undefined;
  }
  if (!(job?.auto_advance_target_stage || "").trim()) {
    return "Auto-advance to ATS stage is turned off. You can change this setting in Role Settings.";
  }
  return "High-match auto-advance stage isn't set. You can change this setting in Role Settings.";
}

export function isJuneManagedAtsStage(stageName, job) {
  if (!canRetryMoveAtsStages(job)) {
    return true;
  }
  const current = (stageName || "").trim();
  if (!current) {
    return true;
  }
  const managed = [
    job?.auto_advance_target_stage,
    job?.auto_advance_high_match_stage,
  ]
    .map((name) => (name || "").trim())
    .filter(Boolean);
  return managed.includes(current);
}

export function getRetryAnalysisDisabledReason(
  applicant,
  { isJobClosed = false, job = null, company = null } = {}
) {
  const stageConfig = job || company;
  if (isJobClosed) {
    return "This job is closed.";
  }
  if (isAnalysisRetryLocked(applicant)) {
    return "Evaluation is already running.";
  }
  const stage = getApplicantAtsStage(applicant);
  if (!isJuneManagedAtsStage(stage, stageConfig)) {
    return stage
      ? `Cannot retry evaluation because this candidate is already in ${stage}.`
      : "Cannot retry evaluation because this candidate is not on a June-managed stage.";
  }
  return undefined;
}

export function canRetryAnalysis(
  applicant,
  { isJobClosed = false, job = null, company = null } = {}
) {
  const stageConfig = job || company;
  return (
    isAnalysisRetryEligible(applicant, { isJobClosed }) &&
    !isAnalysisRetryLocked(applicant) &&
    !getRetryAnalysisDisabledReason(applicant, { isJobClosed, job: stageConfig })
  );
}

export function getApiErrorMessage(data, fallback = "Something went wrong") {
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => item?.msg || item?.detail || (typeof item === "string" ? item : ""))
      .filter(Boolean);
    if (parts.length) {
      return parts.join(" ");
    }
  }
  return fallback;
}

export const RETRY_ANALYSIS_POLL_MS = 5000;
export const RETRY_ANALYSIS_WAIT_TIMEOUT_MS = 6 * 60 * 1000;

const ANALYSIS_IN_PROGRESS = "IN_PROGRESS";
const ANALYSIS_COMPLETED = "COMPLETED";
const ANALYSIS_FAILED = "FAILED";

function normalizeSessionId(sessionId) {
  return sessionId == null ? "" : String(sessionId);
}

export function summarizeRetryStatuses(
  sessionIds,
  applicants,
  observedInProgress
) {
  const bySession = new Map();
  for (const applicant of applicants || []) {
    const fields = getRetryAnalysisFields(applicant);
    if (fields.sessionId) {
      bySession.set(normalizeSessionId(fields.sessionId), fields);
    }
  }

  let completed = 0;
  let failed = 0;
  let pending = 0;

  for (const sessionId of sessionIds) {
    const key = normalizeSessionId(sessionId);
    const status = String(
      bySession.get(key)?.analysisStatus || ""
    ).toUpperCase();

    if (status === ANALYSIS_IN_PROGRESS) {
      observedInProgress.add(key);
      pending += 1;
      continue;
    }

    if (observedInProgress.has(key) && status === ANALYSIS_COMPLETED) {
      completed += 1;
      continue;
    }

    if (observedInProgress.has(key) && status === ANALYSIS_FAILED) {
      failed += 1;
      continue;
    }

    pending += 1;
  }

  return {
    completed,
    failed,
    pending,
    total: sessionIds.length,
  };
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function waitForRetryAnalysesToFinish({
  sessionIds,
  fetchApplicants,
  onProgress,
  intervalMs = RETRY_ANALYSIS_POLL_MS,
  timeoutMs = RETRY_ANALYSIS_WAIT_TIMEOUT_MS,
  now = () => Date.now(),
  sleepFn = sleep,
}) {
  const observedInProgress = new Set();
  const deadline = now() + timeoutMs;

  const tick = async () => {
    const applicants = (await fetchApplicants()) || [];
    const summary = summarizeRetryStatuses(
      sessionIds,
      applicants,
      observedInProgress
    );
    onProgress?.(summary);
    return summary;
  };

  let summary = await tick();
  while (summary.pending > 0 && now() < deadline) {
    await sleepFn(intervalMs);
    summary = await tick();
  }

  if (summary.pending > 0) {
    summary = {
      ...summary,
      failed: summary.failed + summary.pending,
      timedOut: summary.pending,
      pending: 0,
    };
    onProgress?.(summary);
  }

  return summary;
}

export function formatRetryProgressToast(completed, total) {
  return `${completed} of ${total} completed`;
}

export function renderRetryProgressToast(completed, total) {
  return (
    <div className="flex w-full items-center justify-between gap-4">
      <span>Retrying evaluation...</span>
      <span className="shrink-0 text-gray-500">
        {formatRetryProgressToast(completed, total)}
      </span>
    </div>
  );
}

export function formatRetryAnalysisToast({
  move_ats_stages = false,
  ats_stage_skip_reason = null,
} = {}) {
  if (ats_stage_skip_reason) {
    return `Successfully re-evaluated. ${ats_stage_skip_reason}`;
  }
  return move_ats_stages
    ? "Successfully re-evaluated. ATS stages may have been updated."
    : "Successfully re-evaluated. ATS stages were not updated.";
}

export function formatBulkRetryToast({
  enqueued = 0,
  skipped = 0,
  stage_moves_eligible = 0,
  move_ats_stages = false,
} = {}) {
  if (enqueued === 0) {
    if (skipped > 0) {
      return `No evaluations were completed. Skipped ${skipped}.`;
    }
    return "No evaluations were completed.";
  }

  const analysisPart =
    enqueued === 1
      ? "Successfully re-evaluated 1 applicant."
      : `Successfully re-evaluated ${enqueued} applicants.`;

  let message = analysisPart;
  if (move_ats_stages) {
    message +=
      stage_moves_eligible === 1
        ? " ATS stages may have been updated for 1."
        : ` ATS stages may have been updated for ${stage_moves_eligible}.`;
  } else {
    message += " ATS stages were not updated.";
  }
  if (skipped > 0) {
    message += ` Skipped ${skipped} (already running or not eligible).`;
  }
  return message;
}

export function formatPartialRetryToast({
  completed = 0,
  failed = 0,
  skipped = 0,
} = {}) {
  const started = completed + failed;
  let message;
  if (completed === 0) {
    message =
      failed === 1
        ? "No evaluations completed. 1 failed."
        : `No evaluations completed. ${failed} failed.`;
  } else {
    message = `Re-evaluated ${completed} of ${started} applicants.`;
    message += failed === 1 ? " 1 failed." : ` ${failed} failed.`;
  }
  if (skipped > 0) {
    message += ` Skipped ${skipped} (already running or not eligible).`;
  }
  return message;
}
